"use client";

import { ReactNode } from "react";
import Badge from "~/componentsDS/Badge";
import { QuizCTA } from "~/components/features/quiz";
import { Carousel } from "~/componentsDS/Carousel";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import { getElementorOffset } from "~/componentsDS/ElementorLoyout/getElementorOffset";
import Container from "~/componentsDS/Layout/Container";
import Reveal from "~/componentsDS/Reveal";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { CarouselContent, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES, QuizRoute } from "~/constants/quizRoutes";
import { cn } from "~/lib/utils";
import SourceCard, { TSourceCard } from "../components/SourceCard";
import { SOURCE_CARDS } from "../data";

const defaultHeaderElement = (
    <>
        <Reveal>
            <Badge className="text-[14px] md:text-[16px] mb-4 font-medium">Our sources</Badge>
            <Heading as="h2" size="sm" sizes={{ md: "md" }} className="text-primary500">
                Designed for <br className="md:hidden" /> long-term{" "}
                <span className="italic text-[28px] md:text-[36px] font-featureDisplay font-normal">confidence</span>
            </Heading>
        </Reveal>

        <Paragraph className="max-w-[516px] text-[18px] leading-[1.35]">
            Oestra<sup>®</sup> is a prescription bioidentical hormone therapy
            compounded to rigorous U.S. standards.
        </Paragraph>

        <Paragraph className="max-w-[516px] text-[18px] font-medium leading-[1.35]">
            What’s inside — and how it’s made — matters.
        </Paragraph>
    </>
);

type Props = {
    cards?: TSourceCard[];
    cardVariant?: "default" | "compact";
    headerElement?: ReactNode;
    sectionClassName?: string;
    containerVariant?: "elementor" | "new";
    carouselWrapperClassName?: string;
    carouselItemClassName?: string;
    showCtaLink?: boolean;
    buttonClassName?: string;
    ctaLink?: QuizRoute;
    gtm?: {
        eventName?: string;
        widget?: string;
    };
};

const SourcesSection = ({
    cards = SOURCE_CARDS,
    cardVariant = "default",
    headerElement = defaultHeaderElement,
    sectionClassName,
    containerVariant = "elementor",
    carouselWrapperClassName = "box-content mt-4 md:mt-8 overflow-hidden",
    carouselItemClassName = "basis-[88%] md:basis-[47%] lg:basis-[41%] lg:min-w-[590px] shrink-0",
    showCtaLink = true,
    buttonClassName,
    ctaLink = QUIZ_ROUTES.HORMONE_IMBALANCE,
    gtm,
}: Props) => {
    const HeaderContainer = containerVariant === "new" ? Container : ElementorContainer;
    const headerContainerProps = containerVariant === "new"
        ? { className: "flex flex-col" }
        : { innerClassName: "flex flex-col" };
    const CarouselContentComponent = containerVariant === "new" ? CarouselContentVisible : CarouselContent;

    return (
        <section className={cn("pt-12 md:pt-12 md:pb-1 font-suisseBP text-primary500", sectionClassName)}>
            <HeaderContainer {...headerContainerProps}>
                {headerElement}
            </HeaderContainer>

            <div className={carouselWrapperClassName}>
                <Carousel
                    opts={{
                        align: getElementorOffset,
                        skipSnaps: false,
                        loop: true,
                    }}
                    className="w-full mx-auto"
                >
                    <CarouselContentComponent>
                        {cards.map((card, idx) => (
                            <CarouselItem
                                key={card.id ?? idx}
                                className={carouselItemClassName}
                            >
                                <SourceCard {...card} variant={cardVariant} gtm={gtm} />
                            </CarouselItem>
                        ))}
                    </CarouselContentComponent>
                </Carousel>
            </div>

            {showCtaLink && (
                <Container>
                    <div className={cn("mt-8 md:hidden", buttonClassName)}>
                        <QuizCTA
                            className="px-8 w-full"
                            href={ctaLink}
                            eventData={{
                                event_name: gtm?.eventName ?? GTM_EVENT_NAMES.LP_INTERACTION,
                                widget: gtm?.widget ?? "our_sources",
                                meta_data: {
                                    click_text: "See If Oestra® Is Right for You",
                                },
                            }}
                        >
                            <span className="inline-block">
                                See If Oestra<sup>®</sup> Is Right for You
                            </span>
                        </QuizCTA>
                    </div>
                </Container>
            )}
        </section>
    );
}

export default SourcesSection;
